import { createContext, useState, useEffect, useMemo } from "react";
import { createCart, getCart, patchCart, deleteCart, patchCartQuantity } from "./api/cart";
import { getCatalogProducts } from "./api/product";
import { getSettings } from "./api/settings";
import { useAuth0 } from "@auth0/auth0-react";
import toast from "react-hot-toast";
import { serverURL, getCatalogProduct, getMatchingStyle, getPrimaryImage } from "./utils";
import axios from "axios";

export const DataContext = createContext({
  customer: null,
  pricingProducts: [],
  catalogProducts: [],
  visitedPages: [],
  addVisitedPage: [],
  settings: {},
  productsInCart: [],
  getCustomer: () => {},
  getProduct: () => {},
  deleteFromCart: () => {},
  removeManyFromCart: () => {},
  clearCartState: () => {},
  addToCart: async () => {},
  updateQuantityFromCart: () => {},
  adjustManyCartQuantities: () => {},
  syncCartInventory: () => {},
  getSubTotal: () => {},
});

export function DataProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [cartProducts, setCartProducts] = useState([]);
  const [visited, setVisited] = useState({});
  const [configuration, setConfiguration] = useState(null);
  const [pricingProducts, setPricingProducts] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [storeDataLoading, setStoreDataLoading] = useState(true);
  const [storeDataError, setStoreDataError] = useState(null);

  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const getAuthHeaders = async () => {
    const token = await getAccessTokenSilently();
    return {
      "Content-Type": "application/json",
      authorization: `Bearer ${token}`,
    };
  };

  const addVisitedPage = (page) => {
    let pageVisits;
    if (visited[page]) {
      pageVisits = visited[page] + 1;
    } else {
      pageVisits = 1;
    }
    setVisited({ ...visited, [page]: pageVisits });
  };

  const getPricingProducts = async () => {
    const response = await fetch(`${serverURL()}/products/pricing-products`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const products = await response.json();

    const mappedProducts = products.map((product) => {
      return {
        productId: product.productId,
        prices: product.prices,
      };
    });

    return mappedProducts;
  };

  const linkAccounts = async () => {
    try {
      const token = await getAccessTokenSilently();

      const res = await axios.post(
        `${serverURL()}/user/link-user`,
        {
          passwordlessUserId: user.sub,
          email: user.email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("Linking successful:", res.data);
    } catch (err) {
      console.error("Linking failed:", err.response?.data || err.message);
    }
  };

  const getCustomer = async () => {
    const headers = await getAuthHeaders();

    let userId = user.sub;

    if (user && user.sub.startsWith("email|")) {
      await linkAccounts();

      const res = await fetch(`${serverURL()}/user/user-by-email/${user.email}`, {
        method: "GET",
        headers,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message);
      }

      const userResponse = await res.json();
      userId = userResponse.userId;
    }

    const response = await fetch(`${serverURL()}/customers/${userId}`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const customer = await response.json();
    setCustomer(customer);
    return customer;
  };

  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
    window.addEventListener("resize", setVH);
    window.addEventListener("orientationchange", setVH);

    return () => {
      window.removeEventListener("resize", setVH);
      window.removeEventListener("orientationchange", setVH);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setStoreDataLoading(true);
      setStoreDataError(null);

      // Public store data must not depend on successful Auth0 token renewal.
      // An expired customer session must never prevent the storefront from loading.
      let token = null;
      if (isAuthenticated) {
        try {
          token = await getAccessTokenSilently();
        } catch (error) {
          console.warn("Customer token unavailable; loading public storefront data.", error);
        }
      } else {
        try {
          const cart = JSON.parse(localStorage.getItem("greyGullCart")) || [];
          if (active) setCartProducts(cart);
        } catch (error) {
          console.warn("Unable to restore local cart.", error);
        }
      }

      // Each public request is independent so one failure cannot block the others.
      const [prices, settingsResult, catalog] = await Promise.allSettled([
        getPricingProducts(),
        getSettings(),
        getCatalogProducts(token),
      ]);
      if (!active) return;

      if (prices.status === "fulfilled") setPricingProducts(prices.value);
      if (settingsResult.status === "fulfilled") setConfiguration(settingsResult.value);
      if (catalog.status === "fulfilled") setCatalogProducts(catalog.value);

      const failed = [prices, settingsResult, catalog].filter((result) => result.status === "rejected");
      if (failed.length) {
        failed.forEach((result) => console.error("Store data request failed:", result.reason));
        setStoreDataError("Some store information could not be loaded.");
      }
      setStoreDataLoading(false);
    };

    fetchData();
    return () => { active = false; };
  }, [isAuthenticated, getAccessTokenSilently]);

  function getCartItemKey(item) {
    return [item.productId, item.lookupKey, item.size, item.design].filter(Boolean).join("-");
  }

  function hydrateCartProductSlugs(cart) {
    return cart.map((item) => {
      if (item.productUrlSlug) return item;

      const catalogProduct = catalogProducts.find((product) => product.productId === item.productId);

      return {
        ...item,
        productUrlSlug: catalogProduct?.productUrlSlug,
      };
    });
  }

  function removeDuplicates(arr1, arr2) {
    const cartByKey = new Map();
    const bothCarts = hydrateCartProductSlugs([...arr1, ...arr2]);

    bothCarts.forEach((item) => {
      const key = getCartItemKey(item);
      const existingItem = cartByKey.get(key);

      if (!existingItem) {
        cartByKey.set(key, item);
        return;
      }

      cartByKey.set(key, {
        ...existingItem,
        ...item,
        quantity: Math.max(Number(existingItem.quantity || 0), Number(item.quantity || 0)),
        productUrlSlug: item.productUrlSlug || existingItem.productUrlSlug,
      });
    });

    return Array.from(cartByKey.values());
  }

  useEffect(() => {
    if (isAuthenticated) {
      const fetchData = async () => {
        try {
          const loginTime = sessionStorage.getItem("login-time");
          const headers = await getAuthHeaders();

          await getCustomer();

          const cartLocalStorage = JSON.parse(localStorage.getItem("greyGullCart")) || [];
          const cartDatabase = await getCart(user?.sub, headers);
          const shouldMergeLocalCart = !loginTime && cartLocalStorage.length > 0;
          const mergedCarts = shouldMergeLocalCart
            ? removeDuplicates(cartLocalStorage, cartDatabase)
            : hydrateCartProductSlugs(cartDatabase);

          if (shouldMergeLocalCart) {
            if (cartDatabase.length > 0) {
              await patchCart(user.sub, mergedCarts, headers);
            } else {
              await createCart(user.sub, mergedCarts, headers);
            }
          }

          localStorage.removeItem("greyGullCart");

          if (!loginTime) {
            sessionStorage.setItem("login-time", Date.now().toString());
          }

          setCartProducts(mergedCarts);
        } catch (error) {
          // A canceled popup or a stale Auth0 cache can briefly leave the SDK
          // reporting an identity without a usable API refresh token. This is
          // not a checkout error; keep the guest cart and checkout untouched.
          const code = String(error?.error || error?.code || "").toLowerCase();
          const message = String(error?.message || "").toLowerCase();
          const authUnavailable = code.includes("missing_refresh_token") ||
            code.includes("login_required") ||
            code.includes("consent_required") ||
            code.includes("popup_closed") ||
            message.includes("missing refresh token");
          if (authUnavailable) {
            console.warn("Customer session unavailable; guest checkout remains available.");
          } else {
            console.error("Unable to load customer data:", error);
            // Account loading must not display a global error over guest checkout.
          }
        }
      };

      fetchData();
    }
  }, [user?.sub, isAuthenticated, catalogProducts]);

  function getProduct(id) {
    const product = pricingProducts.find((p) => p.productId === id);

    if (product === undefined) {
      console.error("Product data does not exist for ID: " + id);
      return undefined;
    }

    return product;
  }

  function getMatchingCartProducts(id) {
    return cartProducts.filter((product) => product.productId === id);
  }

  function saveUpdatedCart(cart) {
    setCartProducts(cart);

    if (!isAuthenticated) {
      localStorage.setItem("greyGullCart", JSON.stringify(cart));
    } else {
      localStorage.removeItem("greyGullCart");
    }
  }

  async function updateCart(cart) {
    let response = null;
    const hydratedCart = hydrateCartProductSlugs(cart);

    if (user?.sub) {
      const headers = await getAuthHeaders();

      if (hydratedCart.length === 0) {
        response = await deleteCart(user.sub, headers);
      } else if (cartProducts?._id || cartProducts.length > 0) {
        response = await patchCart(user.sub, hydratedCart, headers);
      } else {
        response = await createCart(user.sub, hydratedCart, headers);
      }

      if (response.ok) {
        saveUpdatedCart(hydratedCart);
      }
    } else {
      saveUpdatedCart(hydratedCart);
    }

    return response;
  }

  const getUpdatedQuantityValue = (itemId, quantity, fromCart = false) => {
    let updatedQuantity = null;

    if (!fromCart) {
      const foundProduct = cartProducts.find((cartProduct) => itemId === cartProduct.itemId);
      updatedQuantity = foundProduct.quantity + quantity;
    } else {
      updatedQuantity = quantity;
    }

    return updatedQuantity;
  };

  async function updateQuantity(itemId, quantity, fromCart = false) {
    let response = null;
    const updatedQuantity = getUpdatedQuantityValue(itemId, quantity, fromCart);

    const newCart = cartProducts.map((cartProduct) =>
      itemId === cartProduct.itemId ? { ...cartProduct, quantity: updatedQuantity } : cartProduct,
    );

    if (user?.sub) {
      const headers = await getAuthHeaders();
      response = await patchCartQuantity(user.sub, itemId, updatedQuantity, headers);

      if (response.ok) {
        saveUpdatedCart(newCart);
      }
    } else {
      saveUpdatedCart(newCart);
    }

    return response;
  }

  async function removeManyFromCart(ids) {
    const idSet = new Set(ids);
    const newCart = cartProducts.filter((currentProduct) => !idSet.has(currentProduct.itemId));

    try {
      await updateCart(newCart);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message);
    }
  }

  async function adjustManyCartQuantities(adjustments) {
    const adjustmentMap = new Map(adjustments.map(({ itemId, quantity }) => [itemId, quantity]));

    const newCart = cartProducts.map((currentProduct) => {
      if (!adjustmentMap.has(currentProduct.itemId)) return currentProduct;

      return {
        ...currentProduct,
        quantity: adjustmentMap.get(currentProduct.itemId),
      };
    });

    try {
      await updateCart(newCart);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message);
    }
  }

  async function syncCartInventory(removals = [], adjustments = []) {
    const removalSet = new Set(removals);
    const adjustmentMap = new Map(adjustments.map(({ itemId, quantity }) => [itemId, quantity]));

    const newCart = cartProducts
      .filter((currentProduct) => !removalSet.has(currentProduct.itemId))
      .map((currentProduct) => {
        if (!adjustmentMap.has(currentProduct.itemId)) return currentProduct;

        return {
          ...currentProduct,
          quantity: adjustmentMap.get(currentProduct.itemId),
        };
      });

    try {
      await updateCart(newCart);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message);
    }
  }

  async function deleteFromCart(id) {
    try {
      await removeManyFromCart([id]);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message);
    }
  }

  function clearCartState() {
    setCartProducts([]);
    localStorage.removeItem("greyGullCart");
  }

  async function addToCart(payload) {
    const { productId, productName, productUrlSlug, priceId, design, lookupKey, size, quantity, productCost, thumbnail, availableStock } =
      payload;

    const matchingProducts = getMatchingCartProducts(productId);

    const existingProduct =
      matchingProducts.length > 0 &&
      matchingProducts.find((product) => {
        return product.size === size && product.productName === productName && product.design === design;
      });

    const stockLimit = Number(availableStock ?? 0);

    if (existingProduct) {
      const currentQuantity = Number(existingProduct.quantity || 0);
      let quantityToAdd = Number(quantity || 0);
      let adjusted = false;
      let message = "Product added to cart";

      if (stockLimit > 0) {
        const remainingAllowed = stockLimit - currentQuantity;

        if (remainingAllowed <= 0) {
          toast("No more available in this size");

          return {
            success: false,
            reason: "OUT_OF_STOCK",
            message: "No more available in this size",
          };
        }

        if (quantityToAdd > remainingAllowed) {
          message = `Only ${remainingAllowed} more available`;
          toast(message);
          quantityToAdd = remainingAllowed;
          adjusted = true;
        }
      }

      if (quantityToAdd <= 0) {
        return {
          success: false,
          reason: "OUT_OF_STOCK",
          message: "No more available in this size",
        };
      }

      try {
        await updateExistingProductQuantity(existingProduct.itemId, quantityToAdd);
        return {
          success: true,
          action: "updated",
          quantityAdded: quantityToAdd,
          itemId: existingProduct.itemId,
          productId,
          size,
          adjusted,
          message,
        };
      } catch (error) {
        console.error(error);

        return {
          success: false,
          reason: "API_ERROR",
          message: "Failed to add product to cart",
        };
      }
    }

    let quantityToAdd = Number(quantity || 0);
    let adjusted = false;
    let message = "Product added to cart";

    if (stockLimit > 0 && quantityToAdd > stockLimit) {
      message = `Only ${stockLimit} available`;
      toast(message);
      quantityToAdd = stockLimit;
      adjusted = true;
    }

    if (quantityToAdd <= 0) {
      return {
        success: false,
        reason: "OUT_OF_STOCK",
        message: "No more available in this size",
      };
    }

    const newItemId = Math.floor(Math.random() * 1000000000);

    try {
      await updateCart([
        ...cartProducts,
        {
          productId,
          productUrlSlug,
          itemId: newItemId,
          priceId,
          quantity: quantityToAdd,
          productCost,
          productName,
          design,
          lookupKey,
          size,
          thumbnail,
        },
      ]);

      return {
        success: true,
        action: "added",
        quantityAdded: quantityToAdd,
        itemId: newItemId,
        productId,
        size,
        adjusted,
        message,
      };
    } catch (error) {
      console.error(error);

      return {
        success: false,
        reason: "API_ERROR",
        message: "Failed to add product to cart",
      };
    }
  }

  function updateExistingProductQuantity(itemId, quantity) {
    return updateQuantity(itemId, quantity);
  }

  async function updateQuantityFromCart(itemId, quantity) {
    try {
      await adjustManyCartQuantities([{ itemId, quantity }]);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message);
    }
  }

  function getSubTotal() {
    if (cartProducts.length === 0) return 0;

    return cartProducts.reduce((total, product) => {
      return total + (product.productCost / 100) * product.quantity;
    }, 0);
  }

  const cartProductsForUI = useMemo(() => {
    return cartProducts.map((cartProduct) => {
      const catalogProduct = getCatalogProduct(catalogProducts, cartProduct.productId);
      const style = getMatchingStyle(catalogProduct, cartProduct.lookupKey);
      const thumbnail = getPrimaryImage(style)?.url;

      return {
        ...cartProduct,
        inventory: style?.inventory,
        thumbnail,
      };
    });
  }, [cartProducts, catalogProducts]);

  const contextValue = {
    customer,
    pricingProducts,
    catalogProducts,
    storeDataLoading,
    storeDataError,
    visitedPages: visited,
    addVisitedPage,
    productsInCart: cartProducts,
    cartProductsForUI,
    getCustomer,
    getProduct,
    deleteFromCart,
    removeManyFromCart,
    clearCartState,
    addToCart,
    updateQuantityFromCart,
    adjustManyCartQuantities,
    syncCartInventory,
    getSubTotal,
    settings: configuration,
  };

  return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
}

export default DataProvider;
